import React from 'react';
import { Insight } from '@/api/insightsApi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { chartColors } from './chartColors';
import { CustomTooltip } from './CustomTooltip';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { useRechartsTheme } from '@/hooks/useRechartsTheme';

interface ScheduleDelayRiskVisualizationProps {
  insight: Insight;
}

export const ScheduleDelayRiskVisualization: React.FC<ScheduleDelayRiskVisualizationProps> = ({ insight }) => {
  const { theme } = useAppTheme();
  const chartTheme = useRechartsTheme();
  const activitiesAffected = insight.activitiesAffected || 0;
  const milestonesAffected = insight.milestonesAffected || 0;
  const finishDelta = insight.finishDelta;
  const delayDays = finishDelta ? Math.abs(finishDelta) : 0;

  const delaySeverityData = [
    { range: '0-7 days', count: Math.floor(activitiesAffected * 0.3), color: chartColors.warning },
    { range: '8-14 days', count: Math.floor(activitiesAffected * 0.4), color: chartColors.danger },
    { range: '15-30 days', count: Math.floor(activitiesAffected * 0.2), color: chartColors.danger },
    { range: '30+ days', count: Math.floor(activitiesAffected * 0.1), color: '#dc2626' },
  ];

  const impactTimelineData = [
    { period: 'Baseline', days: 0 },
    { period: 'Current', days: finishDelta || 0 }
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Activities Overdue</div>
          <div className="text-2xl font-bold text-red-400">{activitiesAffected}</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Milestones at Risk</div>
          <div className="text-2xl font-bold text-amber-400">{milestonesAffected}</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Avg Delay</div>
          <div className="text-2xl font-bold text-red-400">{delayDays.toFixed(1)} days</div>
        </div>
      </div>

      {/* Delay Severity Distribution */}
      <div className={`rounded-lg p-4 border ${
        theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <h4 className={`text-sm font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Delay Severity Distribution</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={delaySeverityData}
            margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} />
            <XAxis 
              dataKey="range" 
              tick={{ fill: chartTheme.textSecondary, fontSize: 12 }}
              stroke={chartTheme.gridColor}
            />
            <YAxis 
              tick={{ fill: chartTheme.textSecondary, fontSize: 12 }}
              stroke={chartTheme.gridColor}
              label={{ value: 'Activities', angle: -90, position: 'insideLeft', style: { fill: chartTheme.textSecondary } }}
            />
            <Tooltip 
              content={<CustomTooltip formatter={(value: number) => [`${value} activities`, 'Count']} />}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {delaySeverityData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Impact Timeline */}
      {finishDelta !== undefined && finishDelta !== null && (
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h4 className={`text-sm font-semibold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Schedule Impact</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              data={impactTimelineData}
              margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} />
              <XAxis 
                dataKey="period" 
                tick={{ fill: chartTheme.textSecondary, fontSize: 12 }}
                stroke={chartTheme.gridColor}
              />
              <YAxis 
                tick={{ fill: chartTheme.textSecondary, fontSize: 12 }}
                stroke={chartTheme.gridColor}
                label={{ value: 'Days', angle: -90, position: 'insideLeft', style: { fill: chartTheme.textSecondary } }}
              />
              <Tooltip 
                content={<CustomTooltip formatter={(value: number) => [`${value >= 0 ? '+' : ''}${value.toFixed(1)} days`, 'Impact']} />}
              />
              <Bar 
                dataKey="days" 
                fill={finishDelta < 0 ? chartColors.danger : chartColors.success}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};



